import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import "./ManageNurseRequest.css";
import Widgets from "../../../../widgets";
import { useTranslation } from "react-i18next";
import strings from "../../../../utils/strings";
import Icons from "../../../../Assets/Icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getNurseRequests, getNurseSessions, getSessionNurses, assignSessionNurse, rescheduleSession } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import { matchesRequestSearch, sortByReceivingTimeDesc } from "../../../../modules/NursignCare/ManageRequest/requestListUtils";
import ToastPopup from "../../../../widgets/Toast/toastPopup";
import ChatWidget from "../../../Chat/ChatWidget";

// ── Shared TimePicker (mirrors SessionDetailsPage.jsx) ──────────────────────
const to24hMinutes = (h, m, p) => {
  let hrs = parseInt(h, 10);
  if (p === "PM" && hrs !== 12) hrs += 12;
  if (p === "AM" && hrs === 12) hrs = 0;
  return hrs * 60 + parseInt(m, 10);
};

const TimePicker = ({ value, onChange, minMinutes = null }) => {
  const parse = (v) => {
    if (!v) return { h: "10", m: "00", p: "AM" };
    const match = v.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match) return { h: match[1].padStart(2, "0"), m: match[2], p: match[3].toUpperCase() };
    return { h: "10", m: "00", p: "AM" };
  };
  const { h, m, p } = parse(value);
  const upd = (nh, nm, np) => onChange(`${nh}:${nm} ${np}`);
  const sel = {
    className: "sd-time-select",
    style: {
      border: "1px solid var(--box-border)",
      borderRadius: "0.6rem",
      padding: "0.4rem 0.6rem",
      fontSize: "1.4rem",
      background: "var(--main-bg)",
      cursor: "pointer",
    },
  };
  const isDisabled = (nh, nm, np) =>
    minMinutes !== null && to24hMinutes(nh, nm, np) < minMinutes;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
      <select {...sel} value={h} onChange={(e) => upd(e.target.value, m, p)}>
        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((v) => (
          <option key={v} value={v} disabled={isDisabled(v, m, p)}>
            {v}
          </option>
        ))}
      </select>
      <span style={{ fontWeight: 700 }}>:</span>
      <select {...sel} value={m} onChange={(e) => upd(h, e.target.value, p)}>
        {["00", "15", "30", "45"].map((v) => (
          <option key={v} value={v} disabled={isDisabled(h, v, p)}>
            {v}
          </option>
        ))}
      </select>
      <select {...sel} value={p} onChange={(e) => upd(h, m, e.target.value)}>
        <option value="AM" disabled={isDisabled(h, m, "AM")}>AM</option>
        <option value="PM" disabled={isDisabled(h, m, "PM")}>PM</option>
      </select>
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;
const FULL_LIST_LIMIT = 1000;

const TAB_CONFIG = [
  { key: "new", statuses: ["OPEN", "REJECTED", "ASSIGNED"], session: false },
  { key: "approved", statuses: ["VERIFIED"], session: false },
  { key: "scheduled", statuses: ["SCHEDULED"], session: false },
  { key: "upcoming", statuses: ["CONFIRMED", "ASSIGNED", "CHECKED_IN"], session: true },
  { key: "past", statuses: ["CHECKED_OUT", "COMPLETED"], session: true },
];

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

const ManageNurseRequest = ({ scrollToTop }) => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const breadcrumbs = ["nursingCare", "manageRequests"];
  const paths = [{ path: strings.navigation.manageNurseReq }, strings.navigation.manageNurseReq];
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [listPage, setListPage] = useState(1);
  const modalRef = useRef(null);

  // Nurse assignment modal (upcoming tab)
  const [nurseModal, setNurseModal] = useState({ open: false, sessionId: null, sessionItem: null });
  const [nurseOptions, setNurseOptions] = useState([]);
  const [nurseOptionsLoading, setNurseOptionsLoading] = useState(false);
  const [pendingNurseSelection, setPendingNurseSelection] = useState(null);
  const [nursingInstructions, setNursingInstructions] = useState("");
  const [showNurseConfirm, setShowNurseConfirm] = useState(false);
  const [assignedNurses, setAssignedNurses] = useState({});

  // Reschedule modal state
  const [rescheduleModal, setRescheduleModal] = useState({ open: false, sessionId: null });
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleCheckIn, setRescheduleCheckIn] = useState("10:00 AM");
  const [rescheduleCheckOut, setRescheduleCheckOut] = useState("01:00 PM");
  const [rescheduleErrors, setRescheduleErrors] = useState({});
  const activeList = queryParams.get("activeList") || "new";
  const dispatch = useDispatch();

  const { newRequests, approvedRequests, scheduledRequests, upcomingRequests, pastRequests, totalDocuments } = useSelector((state) => state.manageRequest);

  const activeTabConfig = TAB_CONFIG.find((c) => c.key === activeList) || TAB_CONFIG[0];
  const statusTypes = activeTabConfig.statuses;

  let statuses = queryParams.getAll("status");
  const [selectedStatuses, setSelectedStatuses] = useState(statuses.length > 0 ? statuses : statusTypes);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const urlStatuses = queryParams.getAll("status");
      const effective = urlStatuses.length > 0 ? urlStatuses : statusTypes;
      if (!(selectedStatuses?.length === effective.length && selectedStatuses.every((v, i) => v === effective[i]))) {
        setSelectedStatuses(effective);
      }
    }, 10);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [queryParams]);

  useEffect(() => {
    queryParams.delete("status");
    if (selectedStatuses !== null) {
      selectedStatuses?.forEach((status) => queryParams.append("status", status));
      nav(`${location.pathname}?${queryParams.toString()}`);
    }
  }, [selectedStatuses]);

  const getEffectiveStatuses = () => {
    if (selectedStatuses?.length > 0) return selectedStatuses;
    return activeTabConfig.statuses;
  };

  const loadTabData = useCallback(() => {
    const effectiveStatuses = getEffectiveStatuses();
    setListPage(1);
    if (activeTabConfig.session) {
      dispatch(getNurseSessions({ tab: activeList, statuses: effectiveStatuses, page: 1, limit: FULL_LIST_LIMIT }));
    } else {
      dispatch(getNurseRequests({ tab: activeList, statuses: effectiveStatuses, page: 1, limit: FULL_LIST_LIMIT }));
    }
  }, [activeList, activeTabConfig.session, dispatch, selectedStatuses]);

  const isMounted = useRef(false);
  const skipStatusEffect = useRef(false);

  useEffect(() => {
    skipStatusEffect.current = true;
    const statuses = activeTabConfig.statuses;
    setListPage(1);
    if (activeTabConfig.session) {
      dispatch(getNurseSessions({ tab: activeList, statuses, page: 1, limit: FULL_LIST_LIMIT }));
    } else {
      dispatch(getNurseRequests({ tab: activeList, statuses, page: 1, limit: FULL_LIST_LIMIT }));
    }
  }, [activeList]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (skipStatusEffect.current) {
      skipStatusEffect.current = false;
      return;
    }
    loadTabData();
  }, [selectedStatuses]);

  useEffect(() => {
    setListPage(1);
  }, [searchTerm]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) setShowFilter(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [modalRef]);

  const requestList =
    activeList === "new"
      ? newRequests
      : activeList === "approved"
        ? approvedRequests
        : activeList === "scheduled"
          ? scheduledRequests
          : activeList === "upcoming"
            ? upcomingRequests
            : activeList === "past"
              ? pastRequests
      : [];

  const filteredList = useMemo(() => {
    const sorted = sortByReceivingTimeDesc(requestList);
    if (!searchTerm.trim()) return sorted;
    return sorted.filter((item) => matchesRequestSearch(item, searchTerm));
  }, [requestList, searchTerm]);

  const totalPages = filteredList.length === 0 ? 0 : Math.ceil(filteredList.length / PAGE_SIZE);

  const displayList = useMemo(() => {
    const start = (listPage - 1) * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, listPage]);

  const activeTabCount = searchTerm.trim() ? filteredList.length : totalDocuments || requestList.length;

  useEffect(() => {
    if (totalPages > 0 && listPage > totalPages) {
      setListPage(totalPages);
    }
  }, [listPage, totalPages]);

  const handleActiveChange = (section) => {
    const url = `${strings.navigation.manageNurseReq}?activeList=${encodeURIComponent(section)}`;
    nav(url);
  };

  const handleClick = (pageNumber) => {
    scrollToTop();
    setListPage(pageNumber);
  };

  const handlePrevNext = (direction) => {
    scrollToTop();
    if (direction === "prev" && listPage > 1) setListPage(listPage - 1);
    if (direction === "next" && listPage < totalPages) setListPage(listPage + 1);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (listPage <= 2) {
        pages.push(1, 2, { type: "ellipsis" }, totalPages);
      } else if (listPage >= totalPages - 1) {
        pages.push(1, { type: "ellipsis" }, totalPages - 1, totalPages);
      } else {
        pages.push(1, { type: "ellipsis" }, listPage, { type: "ellipsis" }, totalPages);
      }
    }
    return pages;
  };

  const handleNurseEdit = (e, item) => {
    e.stopPropagation();
    const existing =
      assignedNurses[item._id] ||
      (item.nurse
        ? {
            value: item.nurse._id,
            label: `${item.nurse.firstName?.en || item.nurse.firstName || ""} ${item.nurse.lastName?.en || item.nurse.lastName || ""}`.trim(),
          }
        : null);
    setNurseModal({ open: true, sessionId: item._id, sessionItem: item });
    setPendingNurseSelection(existing);
    setNursingInstructions(item.nursingInstructions || item.nursingInstruction || item.instructions || "");
    setNurseOptions([]);
    setNurseOptionsLoading(true);
    dispatch(getSessionNurses({ sessionId: item._id })).then((res) => {
      setNurseOptionsLoading(false);
      if (res.meta.requestStatus === "fulfilled") {
        setNurseOptions(
          (res.payload.nurses || []).map((n) => ({
            value: n._id,
            label: `${n.firstName?.en || n.firstName || ""} ${n.lastName?.en || n.lastName || ""}`.trim() || n._id,
          })),
        );
      }
    });
  };

  const handleCloseNurseModal = () => {
    setNurseModal({ open: false, sessionId: null, sessionItem: null });
    setPendingNurseSelection(null);
    setNursingInstructions("");
  };

  // "Save" in nurse selection modal → show confirmation popup
  const handleNurseSaveClick = () => {
    if (!pendingNurseSelection) return;
    setShowNurseConfirm(true);
  };

  // User confirmed → call assign API
  const handleConfirmNurseAssign = () => {
    if (!pendingNurseSelection || !nurseModal.sessionId) {
      setShowNurseConfirm(false);
      setNurseModal({ open: false });
      return;
    }
    const sessionId = nurseModal.sessionId;
    const instructions = nursingInstructions.trim();
    setShowNurseConfirm(false);
    setNurseModal({ open: false, sessionId: null, sessionItem: null });

    dispatch(assignSessionNurse({ sessionId, nurseId: pendingNurseSelection.value, nursingInstructions: instructions })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        // Update local display immediately
        setAssignedNurses((prev) => ({ ...prev, [sessionId]: pendingNurseSelection }));
        setNursingInstructions("");
        ToastPopup.success(t("nurseUpdatedSuccess"));
      } else {
        ToastPopup.error(res.error?.message || t("failedToUpdateNurse"));
      }
    });
  };

  // Parses both ISO ("2026-06-17T03:30:00.000Z") and pre-formatted ("17 Jun 2026 at 10:30 AM") startTime values
  const parseSessionDate = (raw) => {
    if (!raw) return null;
    if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) return new Date(raw);
    const d = new Date(raw.includes(" at ") ? raw.replace(" at ", " ") : raw);
    return isNaN(d) ? null : d;
  };

  // Returns true only for CONFIRMED + unassigned sessions whose datetime has already passed
  const isSessionExpired = (item) => {
    if (item.status !== "CONFIRMED") return false;
    const d = parseSessionDate(item.startTime);
    return d !== null && d < new Date();
  };

  const handleOpenReschedule = (e, item) => {
    e.stopPropagation();
    setRescheduleDate("");
    setRescheduleCheckIn("10:00 AM");
    setRescheduleCheckOut("01:00 PM");
    setRescheduleErrors({});
    setRescheduleModal({ open: true, sessionId: item._id });
  };

  const handleCloseRescheduleModal = () => {
    setRescheduleModal({ open: false, sessionId: null });
    setRescheduleErrors({});
  };

// Parse "HH:MM AM/PM" → minutes since midnight
  const parseTimePickerMinutes = (val) => {
    if (!val) return null;
    const match = val.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    return to24hMinutes(match[1], match[2], match[3].toUpperCase());
  };

  const validateReschedule = () => {
    const errors = {};
    const today = new Date().toISOString().split("T")[0];
    if (!rescheduleDate) {
      errors.date = t("dateRequired");
    } else if (rescheduleDate < today) {
      errors.date = t("pastDateNotAllowed");
    }

    const checkInMins = parseTimePickerMinutes(rescheduleCheckIn);
    const checkOutMins = parseTimePickerMinutes(rescheduleCheckOut);

    if (checkInMins === null) {
      errors.checkIn = t("checkInRequired");
    } else if (rescheduleDate === today) {
      const nowMins = new Date().getHours() * 60 + new Date().getMinutes();
      if (checkInMins <= nowMins) errors.checkIn = t("pastDateNotAllowed");
    }

    if (checkOutMins === null) {
      errors.checkOut = t("checkOutRequired");
    }

    if (checkInMins !== null && checkOutMins !== null && checkOutMins <= checkInMins) {
      errors.timeRange = t("invalidTimeRange");
    }

    return errors;
  };

// Convert "HH:MM AM/PM" → "HH:mm" 24h for the API
  const timePickerTo24h = (val) => {
    if (!val) return "";
    const match = val.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return val;
    let h = parseInt(match[1], 10);
    const mins = match[2];
    const period = match[3].toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${mins}`;
  };

  const handleRescheduleConfirm = () => {
    const errors = validateReschedule();
    if (Object.keys(errors).length > 0) {
      setRescheduleErrors(errors);
      return;
    }
    dispatch(rescheduleSession({
      sessionId: rescheduleModal.sessionId,
      body: {
        date: rescheduleDate,
        checkInTime: timePickerTo24h(rescheduleCheckIn),
        checkOutTime: timePickerTo24h(rescheduleCheckOut),
      },
    })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        ToastPopup.success(t("rescheduleSuccess"));
        handleCloseRescheduleModal();
        loadTabData();
      } else {
        ToastPopup.error(res.error?.message || t("rescheduleFailed"));
      }
    });
  };

  const formatSessionDateTime = (startTime) => {
    if (!startTime) return "-";
    // If already formatted (e.g. "23 Jun 2026 at 10:00 AM"), return as-is
    if (typeof startTime === "string" && !/^\d{4}-\d{2}-\d{2}T/.test(startTime)) return startTime;
    // ISO format → parse and format
    const d = new Date(startTime);
    if (isNaN(d)) return startTime;
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${date} at ${time}`;
  };

  const getPatientFromRequest = (item) => {
    return (typeof item.patient === "object" && item.patient) || null;
  };

  const getRequestHn = (item) => {
    const patient = getPatientFromRequest(item);
    return patient?.hnNumber || item.hnNumber || item.hn || "-";
  };

  const formatRequestDateTime = (dateTime) => {
    if (!dateTime) return "";
    if (typeof dateTime === "string" && !/^\d{4}-\d{2}-\d{2}T/.test(dateTime)) return dateTime;

    const d = dateTime instanceof Date ? dateTime : new Date(dateTime);
    if (isNaN(d)) return "";

    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${date} at ${time}`;
  };

  const getDateFromObjectId = (id) => {
    if (!OBJECT_ID_PATTERN.test(id || "")) return null;
    const timestamp = parseInt(id.slice(0, 8), 16) * 1000;
    const date = new Date(timestamp);
    return isNaN(date) ? null : date;
  };

  const getRequestReceivingTime = (item) => {
    const raw = item.requestReceivingTime || item.receivedAt || item.requestedAt || item.submittedAt || item.createdAt;

    const formattedRaw = formatRequestDateTime(raw);
    if (formattedRaw) return formattedRaw;

    return formatRequestDateTime(getDateFromObjectId(item._id));
  };

  const handleRowClick = (item) => {
    switch (activeList) {
      case "approved":
        nav(`${strings.navigation.viewApprovedRequestDetails}?id=${encodeURIComponent(item._id)}&from=approved`, {
          state: {
            patientCondition: item.patientCondition,
            distanceInKm: item.distanceInKm ?? item.zone ?? item.address?.zone,
          },
        });
        break;
      case "scheduled":
        nav(`${strings.navigation.viewSessionDetails}?requestId=${encodeURIComponent(item._id)}&from=scheduled`);
        break;
      case "upcoming":
        nav(`${strings.navigation.viewSessionDetails}?id=${encodeURIComponent(item._id)}&from=upcoming`);
        break;
      case "past":
        nav(`${strings.navigation.viewSessionDetails}?id=${encodeURIComponent(item._id)}&from=past`);
        break;
      default:
        nav(`${strings.navigation.viewNurseReqDetails}?id=${encodeURIComponent(item._id)}`);
        break;
    }
  };

  const renderTableHead = () => {
    switch (activeList) {
      case "upcoming":
      case "past":
        return (
          <tr>
            <th>{t("patientDetails")}</th>
            <th>{t("hn")}</th>
            <th>{t("sessionDateTime")}</th>
            <th>{t("requestReceivingTime")}</th>
            <th>{t("HomecareStaff")}</th>
            <th>{t("status")}</th>
          </tr>
        );
      case "scheduled":
      default:
        return (
          <tr>
            <th>{t("patientDetails")}</th>
            <th>{t("hn")}</th>
            <th>{t("request_id")}</th>
            <th>{t("requestReceivingTime")}</th>
            <th>{t("status")}</th>
          </tr>
        );
    }
  };

  const renderTableRow = (item) => {
    // For new/approved/scheduled list items, name+phone+photo come from relativeInfo (flat strings)
    // For upcoming/past session items, patient object may have fullName
    const rel = item.relativeInfo;
    const pat = getPatientFromRequest(item);
    const displayName =
      (typeof pat === "object" && (pat?.fullName || `${pat?.firstName || ""} ${pat?.lastName || ""}`.trim())) ||
      (rel ? `${rel.firstName || ""} ${rel.lastName || ""}`.trim() : "") ||
      "-";
    const displayPhoto =
      (typeof item.patient === "object" && item.patient?.profilePhoto) ||
      item.relativeInfo?.profilePhoto ||
      (typeof pat === "object" && pat?.profilePhoto) ||
      rel?.profilePhoto ||
      null;
    const displayPhone = (() => {
      const src = (typeof pat === "object" && pat?.countryCode) ? pat : rel;
      if (!src) return "-";
      return src.countryCode ? `+${src.countryCode} ${src.phoneNumber}` : src.phoneNumber || src.email || "-";
    })();

    const profileCell = (
      <td>
        <div className="ppl-td-first">
          <div>
            {displayPhoto ? (
              <img
                src={displayPhoto}
                alt="profile-pic"
                className="ppl-td-profile"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div className="ppl-detials" style={{ display: displayPhoto ? "none" : "flex" }}>
              <img src={Icons.profile} alt="profile-pic" className="ppl-td-profile-none" />
            </div>
          </div>
          <div className="ppl-name-email">
            <section>{displayName}</section>
            <section className="ppl-email">{displayPhone}</section>
          </div>
        </div>
      </td>
    );

    const statusCell = (
      <td className={`ppl-status ${item.status || ""}`}>
        <p>{t(item.status ? item.status : "error")}</p>
      </td>
    );

    switch (activeList) {
      case "past": {
        const nurseObj = item.nurse
          ? {
              label: `${item.nurse.firstName?.en || item.nurse.firstName || ""} ${item.nurse.lastName?.en || item.nurse.lastName || ""}`.trim() || "-",
            }
          : null;
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => handleRowClick(item)}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td className="session-dt-cell">{formatSessionDateTime(item.startTime)}</td>
            <td className="mnr-receiving-time-cell">{getRequestReceivingTime(item)}</td>
            <td>
              <div className="mnr-nurse-pill-readonly">{nurseObj?.label || t("notAssigned")}</div>
            </td>
            {statusCell}
          </tr>
        );
      }
      case "upcoming": {
        const nurseObj =
          assignedNurses[item._id] ||
          (item.nurse
            ? {
                value: item.nurse._id,
                label: `${item.nurse.firstName?.en || item.nurse.firstName || ""} ${item.nurse.lastName?.en || item.nurse.lastName || ""}`.trim(),
              }
            : null);
        const showReschedule = !nurseObj && isSessionExpired(item);
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => { if (item.status === "CHECKED_IN") { return; } handleRowClick(item); }}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td className="session-dt-cell">{formatSessionDateTime(item.startTime)}</td>
            <td className="mnr-receiving-time-cell">{getRequestReceivingTime(item)}</td>
            <td>
              {showReschedule ? (
                <div className="mnr-nurse-pill mnr-reschedule-btn" onClick={(e) => handleOpenReschedule(e, item)}>
                  <span>{t("reschedule")}</span>
                </div>
              ) : (
                <div className={`mnr-nurse-pill ${nurseObj ? "assigned" : ""} ${item.status === "CHECKED_IN" ? "disabled" : ""}`} onClick={(e) => { if (item.status === "CHECKED_IN") { e.stopPropagation(); return; } handleNurseEdit(e, item); }}>
                  <span>{nurseObj?.label || t("notAssigned")}</span>
                  {item.status !== "CHECKED_IN" && <img src={Icons.edit} alt="edit" className="nurse-edit-icon" />}
                </div>
              )}
            </td>
            {statusCell}
          </tr>
        );
      }
      case "approved":
      case "new":
      default:
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => handleRowClick(item)}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td>{item.nursingRequestID || item._id || "-"}</td>
            <td className="mnr-receiving-time-cell">{getRequestReceivingTime(item)}</td>
            {statusCell}
          </tr>
        );
    }
  };

  return (
    <>
      <div className="nurse-req-container">
        <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
        <div className="department-heading">{activeList === "upcoming" || activeList === "past" ? t("sessions") : t("services")}</div>
        {/* Search bar only for non-session tabs */}

        <div className="mnr-search-row">
          <Widgets.SearchBox onSearch={(val) => setSearchTerm(val)} placeholder={t("searchByRequestIdOrHN")} />
        </div>

        <div className="ppl-display">
          <section>
            <section>
              {TAB_CONFIG.map(({ key }) => (
                <span key={key} className={`tab ${activeList === key ? "active" : ""}`} onClick={() => handleActiveChange(key)}>
                  {key === activeList ? t(key, { count: activeTabCount }) : t(key + "Label")}
                </span>
              ))}
            </section>
          </section>
          <div className="mnr-right-controls">
                <div className="ppl-icon-container" ref={modalRef}>
              <section className="ppl-icon-filter" onClick={() => setShowFilter(!showFilter)}>
                <img src={Icons.filterIcon} alt="icon-filter" />
              </section>
              {showFilter && (
                <section className="ppl-status-dropdown">
                  <Widgets.StatusDropdown
                    statusTypes={statusTypes}
                    setShowFilter={setShowFilter}
                    selectedStatuses={selectedStatuses}
                    setSelectedStatuses={setSelectedStatuses}
                    onStatusChange={() => {}}
                  />
                </section>
              )}
            </div>
          </div>
        </div>
        <div className="ppl-list-container">
          <div className="ppl-table-body">
            <table className={`ppl-table ${activeTabConfig.session ? "mnr-session-table" : "mnr-request-table"}`}>
              <thead className="ppl-thead">{renderTableHead()}</thead>
              <tbody className="ppl-tbody">{displayList.map((item) => renderTableRow(item))}</tbody>
            </table>
            {totalPages === 0 && (
              <div className="no-record-container ppl">
                <div className="no-record-icon">
                  <img src={Icons.NoData} alt="No Data Found" />
                </div>
                <div className="no-record-text">{t("noRecordFound")}</div>
              </div>
            )}
          </div>
          <div className="department-pagination-list">
            {totalPages > 0 && (
              <>
                <button onClick={() => handlePrevNext("prev")} disabled={listPage === 1} className="department-pagination-prev-list">
                  <img src={Icons.leftArrow} alt="<" />
                </button>
                {renderPagination().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && handleClick(page)}
                    disabled={page === listPage || page.type === "ellipsis"}
                    className={`department-pagination-btn-list ${page === listPage ? "active-list" : ""} ${page.type === "ellipsis" ? "ellipse" : ""}`}
                  >
                    {typeof page === "number" ? page : <img src={Icons.extraIcon} alt="..." className="department-extra-icon" />}
                  </button>
                ))}
                <button onClick={() => handlePrevNext("next")} disabled={listPage === totalPages} className="department-pagination-next-list">
                  <img src={Icons.rightArrow} alt=">" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {nurseModal.open && !showNurseConfirm && (
        <div className="assign-nurse-modal-overlay" onClick={handleCloseNurseModal}>
          <div className="assign-nurse-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="assign-modal-heading">{t("assignNurse")}</h3>
            <div className="assign-modal-body">
              <span className="assign-modal-label">{t("selectNurse") || "Assign Homecare Staff"}</span>
              <div className="assign-modal-dropdown">
                {nurseOptionsLoading ? (
                  <span className="assign-modal-loading">{t("loading")}…</span>
                ) : nurseOptions.length === 0 ? (
                  <span className="assign-modal-loading">{t("notAvailable")}</span>
                ) : (
                  <Widgets.NewSelectDropdown options={nurseOptions} selectedValue={pendingNurseSelection} onChange={setPendingNurseSelection} placeholder={t("notAssigned")} noMargin disabled={nurseModal.sessionItem?.status === "CHECKED_IN"} />
                )}
              </div>
            </div>
            <div className="assign-modal-body assign-modal-field-stack">
              <label className="assign-modal-label" htmlFor="nursing-instructions">
                {t("nursingInstructions")}
              </label>
              <input
                id="nursing-instructions"
                type="text"
                className="assign-modal-text-input"
                value={nursingInstructions}
                onChange={(e) => setNursingInstructions(e.target.value)}
                placeholder={t("enterNursingInstructions")}
              />
            </div>
            <div className="assign-modal-footer">
              <button className="assign-modal-cancel" onClick={handleCloseNurseModal}>
                {t("cancel")}
              </button>
              <button className="assign-modal-confirm" onClick={handleNurseSaveClick} disabled={!pendingNurseSelection || nurseModal.sessionItem?.status === "CHECKED_IN"}>
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNurseConfirm && (
        <div className="assign-nurse-modal-overlay" onClick={() => setShowNurseConfirm(false)}>
          <div className="assign-nurse-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="assign-modal-heading">{t("confirmUpdate") || "Confirm Update"}</h3>
            {/* <p style={{ fontSize: "1.4rem", color: "var(--grey-text)", margin: "1.2rem 0 2rem" }}>{t("confirmNurseUpdate") || "Are you sure you want to update the Homecare Staff?"}</p> */}
            <div className="assign-modal-footer">
              <button className="assign-modal-cancel" onClick={() => setShowNurseConfirm(false)}>
                {t("cancel")}
              </button>
              <button className="assign-modal-confirm" onClick={handleConfirmNurseAssign} disabled={nurseModal.sessionItem?.status === "CHECKED_IN"}>
                {t("confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ChatWidget />

      {rescheduleModal.open && (
        <div className="assign-nurse-modal-overlay" onClick={handleCloseRescheduleModal}>
          <div className="assign-nurse-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="assign-modal-heading">{t("rescheduleSession")}</h3>
            <div className="assign-modal-body assign-modal-field-stack">
              <label className="assign-modal-label">{t("selectDate")}</label>
              <input
                type="date"
                className="assign-modal-text-input"
                value={rescheduleDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => { setRescheduleDate(e.target.value); setRescheduleErrors((p) => ({ ...p, date: null })); }}
              />
              {rescheduleErrors.date && <span className="assign-modal-error">{rescheduleErrors.date}</span>}
            </div>
            <div className="assign-modal-body assign-modal-field-stack">
              <label className="assign-modal-label">{t("sessionHours")}</label>
              <div className="field-value time-row" style={{ gap: "0.6rem", flexWrap: "wrap" }}>
                <TimePicker
                  value={rescheduleCheckIn}
                  onChange={(val) => { setRescheduleCheckIn(val); setRescheduleErrors((p) => ({ ...p, checkIn: null, timeRange: null })); }}
                  minMinutes={rescheduleDate === new Date().toISOString().split("T")[0] ? new Date().getHours() * 60 + new Date().getMinutes() : null}
                />
                <span className="time-arrow" style={{ fontWeight: 700, alignSelf: "center" }}>→</span>
                <TimePicker
                  value={rescheduleCheckOut}
                  onChange={(val) => { setRescheduleCheckOut(val); setRescheduleErrors((p) => ({ ...p, checkOut: null, timeRange: null })); }}
                />
              </div>
              {rescheduleErrors.checkIn && <span className="assign-modal-error">{rescheduleErrors.checkIn}</span>}
              {rescheduleErrors.checkOut && <span className="assign-modal-error">{rescheduleErrors.checkOut}</span>}
              {rescheduleErrors.timeRange && <span className="assign-modal-error">{rescheduleErrors.timeRange}</span>}
            </div>
            <div className="assign-modal-footer">
              <button className="assign-modal-cancel" onClick={handleCloseRescheduleModal}>{t("cancel")}</button>
              <button className="assign-modal-confirm" onClick={handleRescheduleConfirm}>{t("reschedule")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageNurseRequest;
